import axios, { CancelTokenSource } from 'axios';
import format from 'date-fns/format';
import { configuration } from '../../utils/environment';
import {
  IMTFactsResponse,
  IMTTripPhaseCities,
  IMTTripReservingDetails
} from './apiTypes';

export async function fetchCityFacts(
  erpId: number
): Promise<IMTFactsResponse | undefined> {
  try {
    const response = await axios.get(
      `${configuration.imtErpUrl}/api/v1/facts/city/${erpId}`
    );
    return response.data;
  } catch (err) {
    return undefined;
  }
}

/**
 * Fetches information about cities related to each phase of the trip. Phases are in chronological order listing
 * departure and arrival cities of each phase and type of the phase.
 * @param tripId
 */
export async function fetchTripPhaseCities(
  tripId: number
): Promise<IMTTripPhaseCities[]> {
  try {
    const response = await axios.get(
      `${configuration.imtErpUrl}/api/v1/trips/${tripId}/cities`
    );
    return response.data || [];
  } catch (err) {
    return [];
  }
}

/**
 * Fetches the details which are required to display price box and initiate trip's reservation process
 * in reservation pipeline. Tries to find cheapest priced trip for given search time range. If one can not be found,
 * tries repeatedly to find the trip in future.
 * Full documentation of the ERP's api and search algorithm can be found from the ERP backend's source code
 * with further explanations of each parameter.
 * @param tripId
 * @param searchParams
 * @param searchParams.searchStartDate  The starting date of when the trip is being searched.
 * @param searchParams.searchEndDate  The end date of the time span where the trip is being searched.
 * @param searchParams.prioritizedDuration  The preferred duration of the trip in days to search for.
 * @param searchParams.prioritizePreferredDuration Instead of specified duration prioritize preferred duration from the trip
 * @param source
 */
export async function fetchTripReservingDetails(
  tripId: number,
  searchParams: {
    searchStartDate: Date;
    searchEndDate: Date;
    prioritizedDuration?: number;
    prioritizePreferredDuration?: boolean;
  },
  source?: CancelTokenSource
): Promise<IMTTripReservingDetails | undefined> {
  try {
    const passengers = 2;
    const response = await axios.get(
      `${configuration.imtErpUrl}/api/v1/trips/${tripId}/closestDayPrice`,
      {
        cancelToken: source ? source.token : undefined,
        params: {
          adultsWithoutBus: passengers,
          startDate: format(searchParams.searchStartDate, 'yyyy-MM-dd'),
          endDate: format(searchParams.searchEndDate, 'yyyy-MM-dd'),
          prioritizedDuration: searchParams.prioritizedDuration,
          prioritizePreferredDuration:
            searchParams.prioritizePreferredDuration ?? false
        }
      }
    );

    const { date, durationChoice, price } = response.data.closestDayPrice;
    return {
      tripId,
      date,
      pricePerPassenger: Math.round(parseFloat(price) / passengers),
      durationAsNights: durationChoice.duration - 1,
      extendableHotelPhaseNights: durationChoice.extendableHotelPhaseNights
    };
  } catch (err) {
    return undefined;
  }
}
